// packages/web-ui/src/components/tutorial/TutorialPromptDialog.tsx
//
// Login-time tutorial offer dialog. Shown on every login until the user
// ticks "Don't show this again" (persisted per-user in localStorage).
//
// Matches the ARIA-correct alertdialog pattern used by DeleteConfirmDialog —
// fixed-position scrim, focus the primary action on mount, Esc closes via
// onSkip, backdrop click closes via onSkip.

import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';

export interface TutorialPromptDialogProps {
  open: boolean;
  /** User chose "Show tutorial". `dontShowAgain` reflects checkbox state. */
  onShow: (dontShowAgain: boolean) => void;
  /** User chose "Skip for now" (or Esc / backdrop). */
  onSkip: (dontShowAgain: boolean) => void;
}

export function TutorialPromptDialog({
  open,
  onShow,
  onSkip,
}: TutorialPromptDialogProps): JSX.Element | null {
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const showButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open) {
      setDontShowAgain(false);
      showButtonRef.current?.focus();
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.preventDefault();
        onSkip(dontShowAgain);
      }
    }
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onSkip, dontShowAgain]);

  if (!open) return null;

  return (
    <div
      data-testid="tutorial-prompt-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={(e) => {
        if (e.target === e.currentTarget) onSkip(dontShowAgain);
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="tutorial-prompt-heading"
        aria-describedby="tutorial-prompt-body"
        className="bg-cream-50 border-taupe-500 mx-4 w-full max-w-md rounded-none border p-5"
      >
        <h2
          id="tutorial-prompt-heading"
          data-testid="tutorial-prompt-heading"
          className="text-fg text-base font-extrabold tracking-[-0.01em]"
        >
          Take the TESSA Lean tour?
        </h2>
        <p
          id="tutorial-prompt-body"
          data-testid="tutorial-prompt-body"
          className="text-taupe-700 mt-2 text-sm"
        >
          A short guided tour highlights the main parts of the app: how to send
          requests, review proposed changes, and find help when you need it.
        </p>

        <label
          data-testid="tutorial-prompt-dont-show-again"
          className="mt-4 flex items-center gap-2 text-sm text-taupe-700"
        >
          <input
            type="checkbox"
            checked={dontShowAgain}
            onChange={(e) => setDontShowAgain(e.target.checked)}
            className="border-taupe-500"
          />
          <span>Don&apos;t show this again</span>
        </label>

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            data-testid="tutorial-prompt-skip"
            className="border-taupe-500 hover:border-taupe-900 hover:bg-cream-100 text-fg rounded-none border px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide"
            onClick={() => onSkip(dontShowAgain)}
          >
            Skip for now
          </button>
          <button
            ref={showButtonRef}
            type="button"
            data-testid="tutorial-prompt-show"
            className="bg-audi-red hover:bg-audi-red-hover rounded-none px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-white"
            onClick={() => onShow(dontShowAgain)}
          >
            Show tutorial
          </button>
        </div>
      </div>
    </div>
  );
}
